import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { fieldLabels } from '../profileUpdateFields';

// Login popup for therapists: lists open profile-update requests from the
// office. "Remind me next time" snoozes for this login only — LoginPage clears
// the sessionStorage key on each successful login, so it returns next time.
// Requests are also always reachable at My Clients → Profile Updates.
export const PUR_SNOOZE_KEY = 'pur_snooze';

function requestAge(createdAt) {
  const days = Math.floor((Date.now() - new Date(createdAt)) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export default function ProfileUpdateRequestsPopup() {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(PUR_SNOOZE_KEY) === '1') return undefined;
    let active = true;
    API.get('/therapist/profile-update-requests')
      .then((res) => {
        if (!active) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setRequests(res.data);
          setOpen(true);
        }
      })
      .catch(() => { /* best-effort */ });
    return () => { active = false; };
  }, []);

  if (!open) return null;

  const snooze = () => {
    sessionStorage.setItem(PUR_SNOOZE_KEY, '1');
    setOpen(false);
  };

  const goUpdate = (clientId) => {
    sessionStorage.setItem(PUR_SNOOZE_KEY, '1');
    navigate(`/therapist/client-profile?clientId=${clientId}`);
  };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 32 }}>📋</div>
          <h2 style={{ margin: '4px 0 2px', fontSize: 20 }}>Profile updates needed</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            The office needs some client information filled in
            {requests.length > 1 ? ` (${requests.length} clients)` : ''}.
          </div>
        </div>

        {requests.map((r) => (
          <div key={r.id} style={rowStyle}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{r.client_name}</div>
              <div style={{ fontSize: 13.5 }}>
                Needs: <strong>{fieldLabels(r.requested_fields)}</strong>
              </div>
              {r.note && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  “{r.note}”
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Requested {requestAge(r.created_at)}
              </div>
            </div>
            <button onClick={() => goUpdate(r.client_id)} style={updateBtnStyle}>
              Update now
            </button>
          </div>
        ))}

        <button onClick={snooze} style={snoozeBtnStyle}>
          Remind me next time
        </button>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16,
};
const boxStyle = {
  background: 'var(--white)', borderRadius: 16, padding: '20px 22px', width: '100%',
  maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};
const rowStyle = {
  display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0',
  borderBottom: '1px solid var(--surface-muted)',
};
const updateBtnStyle = {
  flexShrink: 0, background: 'var(--primary-color)', color: '#fff', border: 'none',
  borderRadius: 8, padding: '8px 12px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
};
const snoozeBtnStyle = {
  marginTop: 14, width: '100%', background: 'var(--surface-muted)', color: 'var(--text-color)',
  border: 'none', borderRadius: 10, padding: '11px 14px', fontWeight: 600, fontSize: 14,
  cursor: 'pointer',
};
